import type { AppSettings, CalendarEvent, CalendarView } from '../types'
import { getSupabase } from './supabase'

type RemoteEvent = {
  id: string
  title: string
  start: string
  end: string
  notes: string | null
  color: string
  updated_at: string
  deleted_at: string | null
}

type RemoteSettings = {
  active_tab: string
  calendar_view: CalendarView
  theme?: 'dark' | 'light'
  updated_at: string
}

export function mergeEvents(
  local: CalendarEvent[],
  remote: CalendarEvent[],
): { merged: CalendarEvent[]; toPush: CalendarEvent[] } {
  const map = new Map<string, CalendarEvent>()
  for (const e of local) map.set(e.id, e)
  const toPush: CalendarEvent[] = []
  for (const r of remote) {
    const l = map.get(r.id)
    if (!l) {
      map.set(r.id, r)
      continue
    }
    if (r.updatedAt > l.updatedAt) map.set(r.id, r)
    else if (l.updatedAt > r.updatedAt) toPush.push(l)
  }
  for (const l of local) {
    if (!remote.some((r) => r.id === l.id)) toPush.push(l)
  }
  return { merged: [...map.values()], toPush }
}

export function laterSettings(a: AppSettings, b: AppSettings): AppSettings {
  return a.updatedAt >= b.updatedAt ? a : b
}

function fromRemote(row: RemoteEvent): CalendarEvent {
  return {
    id: row.id,
    title: row.title,
    start: row.start,
    end: row.end,
    notes: row.notes ?? undefined,
    color: row.color,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at,
  }
}

function toRemote(userId: string, e: CalendarEvent) {
  return {
    id: e.id,
    user_id: userId,
    title: e.title,
    start: e.start,
    end: e.end,
    notes: e.notes ?? null,
    color: e.color,
    updated_at: e.updatedAt,
    deleted_at: e.deletedAt ?? null,
  }
}

export async function pullAndPush(
  userId: string,
  localEvents: CalendarEvent[],
  localSettings: AppSettings,
): Promise<{ events: CalendarEvent[]; settings: AppSettings }> {
  const sb = getSupabase()
  if (!sb) return { events: localEvents, settings: localSettings }

  const { data: eventRows, error: evErr } = await sb
    .from('calendar_events')
    .select('id,title,start,end,notes,color,updated_at,deleted_at')
    .eq('user_id', userId)
  if (evErr) throw evErr

  const remoteEvents = (eventRows ?? []).map(fromRemote)
  const { merged, toPush } = mergeEvents(localEvents, remoteEvents)
  if (toPush.length) {
    const { error } = await sb.from('calendar_events').upsert(toPush.map((e) => toRemote(userId, e)))
    if (error) throw error
  }

  const { data: setRow, error: setErr } = await sb
    .from('app_settings')
    .select('active_tab,calendar_view,theme,updated_at')
    .eq('user_id', userId)
    .maybeSingle()
  if (setErr) throw setErr

  let settings = localSettings
  if (setRow) {
    const remoteTheme = (setRow as RemoteSettings).theme
    const remote: AppSettings = {
      activeTab: (setRow as RemoteSettings).active_tab,
      calendarView: (setRow as RemoteSettings).calendar_view,
      theme: remoteTheme === 'light' || remoteTheme === 'dark' ? remoteTheme : localSettings.theme,
      updatedAt: (setRow as RemoteSettings).updated_at,
    }
    settings = laterSettings(localSettings, remote)
    if (settings === localSettings && localSettings.updatedAt !== remote.updatedAt) {
      const { error } = await sb.from('app_settings').upsert({
        user_id: userId,
        active_tab: localSettings.activeTab,
        calendar_view: localSettings.calendarView,
        theme: localSettings.theme,
        updated_at: localSettings.updatedAt,
      })
      if (error) throw error
    }
  } else {
    const { error } = await sb.from('app_settings').upsert({
      user_id: userId,
      active_tab: localSettings.activeTab,
      calendar_view: localSettings.calendarView,
      theme: localSettings.theme,
      updated_at: localSettings.updatedAt,
    })
    if (error) throw error
  }

  return { events: merged, settings }
}

export async function pushEvent(userId: string, event: CalendarEvent): Promise<void> {
  const sb = getSupabase()
  if (!sb) return
  const { error } = await sb.from('calendar_events').upsert(toRemote(userId, event))
  if (error) throw error
}

export async function pushSettings(userId: string, settings: AppSettings): Promise<void> {
  const sb = getSupabase()
  if (!sb) return
  const { error } = await sb.from('app_settings').upsert({
    user_id: userId,
    active_tab: settings.activeTab,
    calendar_view: settings.calendarView,
    theme: settings.theme,
    updated_at: settings.updatedAt,
  })
  if (error) throw error
}
